/**
 * 고정소수점 수학 — 결정론의 1번 규칙.
 *
 * 시뮬레이션 안에서는 부동소수점 연산 결과를 저장하지 않는다.
 * 모든 상태는 정수(Int32)로만 존재하며, 1 단위 = 1/256 픽셀이다.
 *
 * 왜 이게 필요한가:
 *   서버가 클라이언트의 입력 로그만 받아서 전투를 재현하고 점수를 검증하려면
 *   "같은 시드 + 같은 입력 = 항상 같은 결과"가 비트 단위로 성립해야 한다.
 *   JS의 double 연산 자체는 IEEE-754라 기기별로 같지만, Math.sin/cos/sqrt/pow는
 *   엔진·플랫폼별 구현 차이가 허용되는 영역이다. 그래서 정수 연산만 쓴다.
 *
 * 오버플로 예산:
 *   좌표 최대 8192px = 2,097,152 (2^21). 제곱 2^42, 두 개 더해도 2^43 < 2^53 이라
 *   double의 정수 정밀도 안에서 정확하다. 맵은 8192px를 넘지 않는다(assertWorldBounds).
 */

export const FP_BITS = 8;
export const FP_ONE = 1 << FP_BITS; // 256
export const FP_HALF = FP_ONE >> 1;

/** 저작 시점 상수 변환용(픽셀 → 고정소수점). 런타임 상태 계산에는 쓰지 않는다. */
export function fp(px: number): number {
  return Math.round(px * FP_ONE) | 0;
}

/** 렌더링용 변환(고정소수점 → 픽셀). 결과가 시뮬레이션으로 되돌아가면 안 된다. */
export function toPx(v: number): number {
  return v / FP_ONE;
}

/** a * b (둘 다 고정소수점). |a*b| < 2^53 이어야 정확하다. */
export function fpMul(a: number, b: number): number {
  return Math.floor((a * b) / FP_ONE);
}

/** a / b (둘 다 고정소수점). b !== 0. */
export function fpDiv(a: number, b: number): number {
  return Math.floor((a * FP_ONE) / b);
}

/**
 * 정수 제곱근. Math.sqrt를 씨앗으로 쓰되 정수 비교로 교정하므로
 * 씨앗이 1 ulp 틀려도 결과는 항상 유일한 정답으로 수렴한다 → 플랫폼 독립.
 */
export function isqrt(n: number): number {
  if (n <= 0) return 0;
  let x = Math.floor(Math.sqrt(n));
  while (x > 0 && x * x > n) x--;
  while ((x + 1) * (x + 1) <= n) x++;
  return x;
}

/** 벡터 길이(고정소수점). */
export function fpLen(dx: number, dy: number): number {
  return isqrt(dx * dx + dy * dy);
}

/** 제곱 거리. 반경 비교는 되도록 이 함수로 처리해 sqrt를 피한다. */
export function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
}

/** 방향 벡터를 FP_ONE 길이로 정규화해 out[0], out[1]에 넣는다. 길이 0이면 (0,0). */
export function fpNormalize(dx: number, dy: number, out: Int32Array): number {
  const len = fpLen(dx, dy);
  if (len === 0) {
    out[0] = 0;
    out[1] = 0;
    return 0;
  }
  out[0] = Math.floor((dx * FP_ONE) / len);
  out[1] = Math.floor((dy * FP_ONE) / len);
  return len;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function sign(v: number): number {
  return v < 0 ? -1 : v > 0 ? 1 : 0;
}

/** 벡터 길이를 maxLen으로 제한한다. */
export function fpClampLen(dx: number, dy: number, maxLen: number, out: Int32Array): void {
  const lenSq = dx * dx + dy * dy;
  if (lenSq <= maxLen * maxLen) {
    out[0] = dx;
    out[1] = dy;
    return;
  }
  const len = isqrt(lenSq);
  out[0] = Math.floor((dx * maxLen) / len);
  out[1] = Math.floor((dy * maxLen) / len);
}

/** 개발용 가드: 좌표가 오버플로 예산 안에 있는지 확인한다. */
export const FP_MAX_COORD = 8192 * FP_ONE;
export function assertWorldBounds(wPx: number, hPx: number): void {
  if (wPx * FP_ONE > FP_MAX_COORD || hPx * FP_ONE > FP_MAX_COORD) {
    throw new Error(`맵이 너무 큽니다(최대 8192px): ${wPx}x${hPx}`);
  }
}
